import React, { useState } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import Page from 'components/page/page';
import CauseTable from 'components/cause-table/cause-table';
import { useDonations } from 'contexts/donation-context';
import { useAuth } from 'contexts/auth-context';
import Panel from 'components/panel/panel';
import ConfirmationModal from 'components/confirmation-modal/confirmation-modal';
import LastNonProfit from 'components/last-non-profit/last-non-profit';
import { PanelLoader } from 'components/loader/loader';
import { formatMoney } from 'utils/money';
import { useTheme } from 'emotion-theming';
import useScroll from 'hooks/use-scroll';
import { css } from 'emotion';
import './redeem-page.scss';

const RedeemHeaderImage = ({ src, alt = '' }) => {
  return (
    <div
      className={css`
        display: inline-block;
        height: 75%;
      `}
    >
      <img
        src={src}
        alt={alt}
        className={css`
          flex: none;
          display: block;
          max-width: 100%;
          transition: 120ms all;
          height: 100%;
          margin-right: 1.5rem;
          @media (max-width: 850px) {
            margin-right: 1rem;
          }
        `}
      />
    </div>
  );
};

const RedeemHeaderContainer = ({ children }) => (
  <div
    className={css`
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-start;
    `}
  >
    {children}
  </div>
);

const RedeemHeader = ({ children, isSmall = false }) => {
  const { general } = useTheme();

  return (
    <header
      className={css`
        position: fixed;
        width: 100%;
        height: ${isSmall ? '4rem' : '6rem'};
        background: #fff;
        top: 3rem;
        left: 0;
        box-shadow: ${general.headerBoxShadow};
        display: flex;
        justify-content: center;
        align-items: stretch;
        transition: 120ms all;
        z-index: 1000;
        @media (max-width: 850px) {
          height: ${isSmall ? '4rem' : '5rem'};
        }
      `}
    >
      <section
        className={css`
          max-width: 52rem;
          width: 100%;
          padding: 0 1.25rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        `}
      >
        {children}
      </section>
    </header>
  );
};

function RedeemPage() {
  const [isHeaderSmall, setHeaderSmall] = useState(false);
  useScroll({
    onScroll: (scroll) => {
      setHeaderSmall(scroll.y > 8);
    },
  });

  const { redeemID } = useParams();
  const {
    donationList,
    donationLoadingState,
    donationLoadingStates,
  } = useDonations();

  const { colors } = useTheme();

  const { user } = useAuth();

  const [selectedCause, setSelectedCause] = useState(null);

  const [accountID, sponsorID] = redeemID.split('-').map((id) => Number(id));
  const donationObj = donationList.find((item) => {
    return (
      item.StoreAccount.AccountID === accountID &&
      item.SponsorAccount.AccountID === sponsorID
    );
  });

  if (!donationList || donationLoadingState !== donationLoadingStates.LOADED) {
    return (
      <Page className="redeem-page">
        <PanelLoader />
      </Page>
    );
  }

  if (donationList && !donationObj) {
    return <Redirect to="/redeem" />;
  }

  return (
    <Page className="redeem-page">
      <RedeemHeader isSmall={isHeaderSmall}>
        <RedeemHeaderContainer>
          <RedeemHeaderImage
            src={donationObj.StoreAccount.AccountLogo.Small}
            alt={donationObj.StoreAccount.AccountName}
          />
          <RedeemHeaderImage
            src={donationObj.SponsorAccount.AccountLogo.Small}
            alt={donationObj.SponsorAccount.AccountName}
          />
        </RedeemHeaderContainer>
        <span
          className={css`
            display: inline-block;
            color: ${colors.lightGreen};
            transition: 120ms all;
            font-size: ${isHeaderSmall ? '2rem' : '2.5rem'};
            font-weight: bold;
            @media (max-width: 850px) {
              font-size: ${isHeaderSmall ? '1.75rem' : '2.25rem'};
            }
          `}
        >
          {formatMoney(donationObj.TotalDonations)}
        </span>
      </RedeemHeader>
      <Panel
        className={css`
          margin-top: 6rem;
          @media (max-width: 850px) {
            margin-top: 5rem;
          }
        `}
      >
        <h1>Direct your Donation</h1>
        <p>
          These donations are proudly provided by{' '}
          <strong>{donationObj.SponsorAccount.AccountName}</strong> in support
          of local non-profits. {donationObj.SponsorAccount.AccountName} has
          pledged $250,000 in donations to local non-profits for 1 million
          reuses of reusable bags. Learn more about{' '}
          {donationObj.SponsorAccount.AccountName} at{' '}
          <a
            href={`${donationObj.SponsorAccount.AccountWebsite}?ref=reuse2give`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {donationObj.SponsorAccount.AccountWebsite}
          </a>
          .
        </p>
      </Panel>

      {donationObj?.LastCause?.CompanyID > 0 && (
        <Panel>
          <LastNonProfit
            nonProfit={donationObj.LastCause}
            setSelectedCause={setSelectedCause}
          />
        </Panel>
      )}
      <CauseTable
        accountUID={donationObj.StoreAccount.AccountUUID}
        userLatitude={user.Latitude}
        userLongitude={user.Longitude}
        userZipCode={user.ZipCode}
        causeView={donationObj.CauseView}
        setSelectedCause={setSelectedCause}
      />
      <ConfirmationModal
        sponsor={donationObj?.SponsorAccount}
        selectedCause={selectedCause}
        hidden={!selectedCause}
        closeModal={() => setSelectedCause(null)}
        amount={formatMoney(donationObj.TotalDonations)}
        donationObj={donationObj}
      />
    </Page>
  );
}

export default RedeemPage;
